import React from 'react'

import { Box, Stack, useTheme } from '@mui/material'
import { Skeleton } from '@repo/ui'

import { PageContainer } from 'components/UI/PageContainer'
import { PROSE_MAX_WIDTH } from 'constants/layout'
import { AVATAR_ALIGN } from 'pages/Profile/layout'

/** One labeled field placeholder — a short label bar over a full-width input bar. */
const FieldSkeleton = () => (
  <Stack gap={1}>
    <Skeleton variant='text' width={110} height={16} />
    <Skeleton variant='rounded' width='100%' height={44} />
  </Stack>
)

/**
 * Loading placeholder for the profile page. Mirrors the real layout 1:1 (avatar,
 * General fields, Log in, Connected accounts, Log out) so there is zero layout
 * shift when the data arrives (working-conventions §19).
 */
export const ProfileSkeleton = () => {
  const theme = useTheme()
  return (
    <Stack sx={{ background: theme.palette.background.paper, pt: { xs: 4, md: 6 }, pb: { xs: 4, md: 8 } }}>
      {/* Same container + reading measure as the real page, so §19's footprint
          parity survives the move to the shared column. */}
      <PageContainer>
      <Box sx={{ width: '100%', maxWidth: PROSE_MAX_WIDTH, mx: 'auto' }}>
        {/*
          Title only — no back-link bar. Profile is a primary destination reached
          from the nav (bottom bar on a phone, left rail on a desktop), so the
          real page renders no back-link and a placeholder for one would push
          every row below it down by ~32px and then snap back on load.
        */}
        <Skeleton variant='text' width={90} height={28} sx={{ mb: 4 }} />

        {/*
          Alignment comes from the SAME constant the real page reads
          (`pages/Profile/layout`), so this cannot drift from it — it had, and the
          avatar visibly jumped sideways when the profile arrived.
        */}
        <Stack alignItems={AVATAR_ALIGN} mb={4}>
          <Skeleton variant='circular' width={80} height={80} />
        </Stack>

        {/* General */}
        <Skeleton variant='text' width={100} height={24} sx={{ mb: 2 }} />
        <Stack gap={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <FieldSkeleton key={`g${i}`} />
          ))}
        </Stack>

        <Box sx={{ height: 4, backgroundColor: theme.palette.grey[50], my: 3, borderRadius: '8px' }} />

        {/* Log in */}
        <Skeleton variant='text' width={70} height={24} sx={{ mb: 2 }} />
        <Stack gap={2}>
          <FieldSkeleton />
          <FieldSkeleton />
        </Stack>

        {/* Connected accounts */}
        <Skeleton variant='text' width={180} height={24} sx={{ mt: 3, mb: 2 }} />
        <Stack gap={1.5}>
          <Skeleton variant='rounded' width='100%' height={52} />
          <Skeleton variant='rounded' width='100%' height={52} />
        </Stack>

        {/* Log out */}
        <Skeleton variant='rounded' width={120} height={40} sx={{ mt: 4 }} />
      </Box>
      </PageContainer>
    </Stack>
  )
}
